import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
    Box,
    Grid,
    Card,
    CardContent,
    CardActionArea,
    Typography,
    Chip,
    Pagination,
    CircularProgress,
    InputAdornment,
    TextField,
    useTheme,
    Divider,
} from "@mui/material";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import axiosClient from "../../axios";
import BrokerInspectionDetail from "./BrokerInspectionDetail";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PolicyOutlinedIcon from "@mui/icons-material/PolicyOutlined";
import SearchIcon from "@mui/icons-material/Search";

const TYPE_CONFIG = {
    new: {
        label: "New Surveys",
        color: "#6870fa",
        subtitle: "Surveys loaded by Occusurv — requirements email not yet sent.",
    },
    active: {
        label: "Active Surveys",
        color: "#4cceac",
        subtitle: "Requirements in progress — no escalations, not yet overdue.",
    },
    overdue: {
        label: "Overdue Surveys",
        color: "#db4f4a",
        subtitle: "At least one requirement has passed its expected completion date.",
    },
    escalated: {
        label: "Escalated Surveys",
        color: "#FF9800",
        subtitle: "Surveys flagged for escalation attention.",
    },
    submitted: {
        label: "Submitted for Review",
        color: "#00BCD4",
        subtitle: "All requirements submitted — awaiting insurer sign off.",
    },
    completed: {
        label: "Completed Surveys",
        color: "#2e7c67",
        subtitle: "Fully completed and signed off by the insurer.",
    },
    archived: {
        label: "Archived Surveys",
        color: "#858585",
        subtitle: "Archived surveys no longer in active workflow.",
    },
};

const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    if (Number.isNaN(d)) return "—";
    return d.toLocaleDateString("en-ZA", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const getLastReminderDate = (reminderStartDate, reminderDays) => {
    if (!reminderStartDate) return null;

    const start = new Date(reminderStartDate);
    if (Number.isNaN(start.getTime())) return null;

    const intervalDays = Number(reminderDays);
    if (!Number.isFinite(intervalDays) || intervalDays <= 0) return start;

    const today = new Date();
    start.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    const diffMs = today.getTime() - start.getTime();
    if (diffMs <= 0) return start;

    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const cycles = Math.floor(diffDays / intervalDays);
    const lastReminder = new Date(start);
    lastReminder.setDate(start.getDate() + cycles * intervalDays);
    return lastReminder;
};

const InspectionCard = ({ inspection, accentColor, onClick, showReminderBadges, disabled }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const lastReminderDate = getLastReminderDate(
        inspection.reminder_start_date,
        inspection.reminder_days
    );

    return (
        <Card
            variant="outlined"
            sx={{
                borderLeft: `4px solid ${accentColor}`,
                borderRadius: 0,
                bgcolor: colors.primary[400],
                height: "100%",
                display: "flex",
                flexDirection: "column",
            }}
        >
            <CardActionArea
                onClick={disabled ? undefined : onClick}
                disabled={disabled}
                sx={{
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "stretch",
                    transition: "box-shadow 0.2s, background-color 0.2s",
                    ...(!disabled && {
                        "&:hover": {
                            boxShadow: `0 0 16px ${accentColor}44`,
                            bgcolor: `${accentColor}0a`,
                        },
                    }),
                    cursor: disabled ? "default" : "pointer",
                }}
            >
                <CardContent sx={{ flexGrow: 1 }}>
                {/* Policy holder name */}
                <Typography
                    variant="h5"
                    color={colors.grey[100]}
                    fontWeight="bold"
                    gutterBottom
                    sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {inspection.audit_policy_holder || "—"}
                </Typography>

                <Divider sx={{ mb: 1.5, borderColor: colors.grey[700] }} />

                <Grid container spacing={1.5}>
                    {/* Policy number */}
                    <Grid item xs={12} sm={6}>
                        <Box display="flex" alignItems="center" gap={0.5} mb={0.25}>
                            <PolicyOutlinedIcon sx={{ fontSize: 14, color: colors.grey[400] }} />
                            <Typography variant="caption" color={colors.grey[400]}>
                                Policy No
                            </Typography>
                        </Box>
                        <Typography variant="body2" color={colors.grey[200]}>
                            {inspection.audit_policy_number || "—"}
                        </Typography>
                    </Grid>

                    {/* Survey date */}
                    <Grid item xs={12} sm={6}>
                        <Box display="flex" alignItems="center" gap={0.5} mb={0.25}>
                            <CalendarTodayOutlinedIcon
                                sx={{ fontSize: 14, color: colors.grey[400] }}
                            />
                            <Typography variant="caption" color={colors.grey[400]}>
                                Survey Date
                            </Typography>
                        </Box>
                        <Typography variant="body2" color={colors.grey[200]}>
                            {formatDate(inspection.audit_survey_date)}
                        </Typography>
                    </Grid>

                    {/* Insurer */}
                    <Grid item xs={12}>
                        <Box display="flex" alignItems="center" gap={0.5} mb={0.25}>
                            <BusinessOutlinedIcon sx={{ fontSize: 14, color: colors.grey[400] }} />
                            <Typography variant="caption" color={colors.grey[400]}>
                                Insurer
                            </Typography>
                        </Box>
                        <Typography
                            variant="body2"
                            color={colors.grey[200]}
                            sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        >
                            {inspection.audit_insurer_name || "—"}
                        </Typography>
                    </Grid>

                    {/* Physical address */}
                    <Grid item xs={12}>
                        <Box display="flex" alignItems="flex-start" gap={0.5} mt={0.25}>
                            <LocationOnOutlinedIcon
                                sx={{ fontSize: 14, color: colors.grey[400], mt: "2px", flexShrink: 0 }}
                            />
                            <Typography
                                variant="body2"
                                color={colors.grey[300]}
                                sx={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                }}
                            >
                                {inspection.audit_physical_address || "—"}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>

                {/* Reminder badges */}
                {showReminderBadges && inspection.reminder_start_date && (
                    <Box mt={1.5} display="flex" gap={1} flexWrap="wrap">
                        <Chip
                            label={`First Reminder sent: ${formatDate(inspection.reminder_start_date)}`}
                            size="small"
                            sx={{
                                bgcolor: `${accentColor}1a`,
                                color: accentColor,
                                fontWeight: 600,
                                fontSize: "0.7rem",
                            }}
                        />
                        <Chip
                            label={`Last Reminder sent: ${formatDate(lastReminderDate)}`}
                            size="small"
                            sx={{
                                bgcolor: `${accentColor}1a`,
                                color: accentColor,
                                fontWeight: 600,
                                fontSize: "0.7rem",
                            }}
                        />
                    </Box>
                )}
                </CardContent>
            </CardActionArea>
        </Card>
    );
};

export default function BrokerInspections() {
    const { type } = useParams();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const config = TYPE_CONFIG[type] || TYPE_CONFIG.new;
    const showReminderBadges = ["active", "overdue", "escalated", "submitted"].includes(type);

    const [inspections, setInspections] = useState([]);
    const [meta, setMeta] = useState({ total: 0, last_page: 1, current_page: 1 });
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);

    // Live search
    const [searchQuery, setSearchQuery] = useState("");

    // Detail dialog
    const [selectedInspection, setSelectedInspection] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);

    // Reset page + search when navigating to a different type
    useEffect(() => {
        setPage(1);
        setSearchQuery("");
        setDetailOpen(false);
        setSelectedInspection(null);
    }, [type]);

    useEffect(() => {
        let cancelled = false;

        const fetchInspections = async () => {
            setLoading(true);
            try {
                const res = await axiosClient.get(
                    `/inspections/broker_inspections?type=${type}&per_page=24&page=${page}`
                );
                if (!cancelled && res?.data) {
                    setInspections(res.data.data?.data || []);
                    setMeta({
                        total: res.data.data?.total ?? 0,
                        last_page: res.data.data?.last_page ?? 1,
                        current_page: res.data.data?.current_page ?? 1,
                    });
                }
            } catch {
                if (!cancelled) {
                    setInspections([]);
                    setMeta({ total: 0, last_page: 1, current_page: 1 });
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchInspections();
        return () => { cancelled = true; };
    }, [type, page]);

    // Client-side filter by policy holder or policy number
    const filteredInspections = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return inspections;
        return inspections.filter(
            (insp) =>
                (insp.audit_policy_holder || "").toLowerCase().includes(q) ||
                (insp.audit_policy_number || "").toLowerCase().includes(q)
        );
    }, [inspections, searchQuery]);

    const handleCardClick = (inspection) => {
        setSelectedInspection(inspection);
        setDetailOpen(true);
    };

    return (
        <Box m="20px">
            {/* Page header */}
            <Header title={config.label} subtitle={config.subtitle} />

            {/* Search bar + count row */}
            <Box mt={2} mb={2} display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <TextField
                    size="small"
                    placeholder="Search by policy holder or policy number…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: colors.grey[400], fontSize: 18 }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        minWidth: 320,
                        "& .MuiOutlinedInput-root": {
                            bgcolor: colors.primary[400],
                            "& fieldset": { borderColor: `${config.color}44` },
                            "&:hover fieldset": { borderColor: `${config.color}88` },
                            "&.Mui-focused fieldset": { borderColor: config.color },
                        },
                        "& input": { color: colors.grey[100] },
                        "& input::placeholder": { color: colors.grey[500] },
                    }}
                />
                <Chip
                    label={(() => {
                        const total = meta.total;
                        const suffix = total === 1 ? "" : "s";
                        return searchQuery
                            ? `${filteredInspections.length} of ${total} survey${suffix}`
                            : `${total} survey${suffix}`;
                    })()}
                    sx={{
                        bgcolor: `${config.color}1a`,
                        color: config.color,
                        fontWeight: 700,
                        border: `1px solid ${config.color}55`,
                    }}
                />
            </Box>

            {/* Loading spinner */}
            {loading && (
                <Box display="flex" justifyContent="center" mt={8}>
                    <CircularProgress sx={{ color: config.color }} />
                </Box>
            )}

            {/* Empty state */}
            {!loading && filteredInspections.length === 0 && (
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    mt={10}
                    gap={1}
                >
                    <Typography variant="h4" color={colors.grey[500]}>
                        {searchQuery
                            ? `No results for "${searchQuery}"`
                            : `No ${config.label.toLowerCase()} found.`}
                    </Typography>
                    <Typography variant="body2" color={colors.grey[600]}>
                        {searchQuery ? "Try a different search term." : config.subtitle}
                    </Typography>
                </Box>
            )}

            {/* Inspection cards */}
            {!loading && filteredInspections.length > 0 && (
                <Grid container spacing={2}>
                    {filteredInspections.map((inspection) => (
                        <Grid item xs={12} sm={6} md={4} key={inspection.id}>
                            <InspectionCard
                                inspection={inspection}
                                accentColor={config.color}
                                onClick={() => handleCardClick(inspection)}
                                showReminderBadges={showReminderBadges}
                                disabled={type === "new"}
                            />
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Pagination — hidden while search filter is active */}
            {!loading && !searchQuery && meta.last_page > 1 && (
                <Box display="flex" justifyContent="center" mt={4} mb={2}>
                    <Pagination
                        count={meta.last_page}
                        page={meta.current_page}
                        onChange={(_, val) => setPage(val)}
                        sx={{
                            "& .MuiPaginationItem-root": { color: colors.grey[100] },
                            "& .Mui-selected": {
                                bgcolor: `${config.color}2a !important`,
                                color: `${config.color} !important`,
                            },
                        }}
                    />
                </Box>
            )}

            {/* Detail dialog */}
            <BrokerInspectionDetail
                inspection={selectedInspection}
                open={detailOpen}
                onClose={() => setDetailOpen(false)}
                accentColor={config.color}
            />
        </Box>
    );
}
